<script setup lang="ts">
import type { Middleware, ReferenceElement } from '@floating-ui/vue'
import { arrow, autoPlacement, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue'
import { isHoverPointer } from '@ui/utils/hover'
import { Placements } from '@ui/utils/placements'
import { onClickOutside, useActiveElement, useEventListener, useTimeoutFn } from '@vueuse/core'
import { computed, ref, useId, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    label?: string
    disabled?: boolean
    maxWidth?: string
    delayEnter?: number
    mobile?: boolean
    placement?: Placements
    placements?: Placements[]
    mode?: 'hover' | 'click' | 'both' | 'manual'
    initValue?: boolean
    strategy?: 'fixed' | 'absolute'
    teleport?: boolean
    offset?: number
    zIndex?: number
    /** Position against a separate element or virtual boundary while keeping trigger interactions. */
    reference?: ReferenceElement
    middleware?: Middleware[]
  }>(),
  {
    label: '',
    disabled: false,
    maxWidth: 'unset',
    delayEnter: 100,
    mobile: true,
    placements: () => [Placements.Top, Placements.Bottom],
    mode: 'hover',
    initValue: false,
    strategy: 'absolute',
    teleport: false,
    offset: 4,
    zIndex: 150,
    middleware: () => [],
  },
)

const slots = defineSlots<{
  default?: () => void
  tooltip?: (props: { close: () => void }) => void
  link?: (props: { close: () => void }) => void
}>()
const show = defineModel<boolean | undefined>('show', { default: undefined })
const reference = ref<HTMLElement | null>(null)
const floating = ref<HTMLElement | null>(null)
const floatingArrow = ref<HTMLElement | null>(null)
const tooltipId = useId()
const isOpenByClick = ref(props.initValue)
const isOpenByTouch = ref(false)
const isHovered = ref(false)
const isFloatingHovered = ref(false)
const { start: startEnter, stop: stopEnter } = useTimeoutFn(
  () => {
    isHovered.value = true
  },
  () => props.delayEnter,
  { immediate: false },
)
const { start: startLeave, stop: stopLeave } = useTimeoutFn(
  () => {
    isHovered.value = false
  },
  100,
  { immediate: false },
)
const { start: startFloatingLeave, stop: stopFloatingLeave } = useTimeoutFn(
  () => {
    isFloatingHovered.value = false
  },
  100,
  { immediate: false },
)
const activeElement = useActiveElement()
const isFocused = computed(() => {
  const active = activeElement.value
  return Boolean(active && (reference.value?.contains(active) || floating.value?.contains(active)))
})

watch(floating, element => {
  if (!element) {
    stopFloatingLeave()
    isFloatingHovered.value = false
  }
})

function onPointerEnter(event: PointerEvent) {
  if (!isHoverPointer(event)) {
    return
  }
  stopLeave()
  if (props.delayEnter) {
    startEnter()
  } else {
    isHovered.value = true
  }
}

function onPointerLeave(event: PointerEvent) {
  if (event.pointerType === 'touch') {
    return
  }
  stopEnter()
  startLeave()
}

function onFloatingPointerEnter(event: PointerEvent) {
  if (!isHoverPointer(event)) {
    return
  }
  stopFloatingLeave()
  isFloatingHovered.value = true
}

function onFloatingPointerLeave(event: PointerEvent) {
  if (event.pointerType === 'touch') {
    return
  }
  startFloatingLeave()
}

const interactionOpen = computed(() => {
  const hovered = isHovered.value || isFloatingHovered.value || isFocused.value || isOpenByTouch.value
  switch (props.mode) {
    case 'hover':
      return hovered
    case 'click':
      return isOpenByClick.value
    case 'both':
      return isOpenByClick.value || hovered
    default:
      return false
  }
})
const open = computed(() => !props.disabled && (show.value ?? interactionOpen.value))

watch(interactionOpen, value => {
  if (props.mode !== 'manual') {
    show.value = value
  }
})

// A tap fires touchstart and then a compatibility click; that click must not undo the tap.
// A tap may end without a click, so mouse and keyboard input forget it.
let tapHandled = false
// State before the tap, restored when the browser cancels it (e.g. to scroll).
let beforeTap: { touch: boolean; click: boolean; show: boolean | undefined } | undefined

function forgetTap() {
  tapHandled = false
  beforeTap = undefined
}

function onTouchCancel() {
  if (tapHandled && beforeTap) {
    isOpenByTouch.value = beforeTap.touch
    isOpenByClick.value = beforeTap.click
    show.value = beforeTap.show
  }
  forgetTap()
}

function onPointerDown(event: PointerEvent) {
  if (event.pointerType !== 'touch') {
    forgetTap()
  }
}

function onOpen(event: Event) {
  if (tapHandled) {
    forgetTap()
    return
  }
  if (props.disabled || (props.mode !== 'click' && props.mode !== 'both')) {
    return
  }
  if (event.target instanceof Node && floating.value?.contains(event.target)) {
    return
  }
  isOpenByClick.value = true
  show.value = true
}

function onClose() {
  isOpenByClick.value = false
  isOpenByTouch.value = false
  show.value = false
}

function onTouch(event: TouchEvent) {
  if (event.target instanceof Node && floating.value?.contains(event.target)) {
    return
  }
  if (!props.disabled && (props.mode === 'hover' || props.mode === 'both')) {
    tapHandled = true
    beforeTap = { touch: isOpenByTouch.value, click: isOpenByClick.value, show: show.value }
    isOpenByTouch.value = !open.value
    if (!isOpenByTouch.value) {
      isOpenByClick.value = false
    }
    show.value = isOpenByTouch.value
  }
}

onClickOutside(
  reference,
  () => {
    if (props.mode === 'click' || props.mode === 'both' || isOpenByTouch.value) {
      onClose()
    }
  },
  { ignore: [floating] },
)

useEventListener('keydown', event => {
  if (open.value && props.mode !== 'manual' && event.key === 'Escape') {
    onClose()
  }
})

const positioningReference = computed(() => props.reference ?? reference.value)
const { floatingStyles, middlewareData, placement, update } = useFloating(positioningReference, floating, {
  strategy: computed(() => props.strategy),
  placement: computed(() => props.placement ?? props.placements[0] ?? Placements.Top),
  open,
  whileElementsMounted: autoUpdate,
  middleware: computed(() => [
    offset(props.offset),
    props.placement
      ? flip({ padding: 12, fallbackPlacements: props.placements })
      : autoPlacement({ padding: 8, allowedPlacements: props.placements }),
    shift({ padding: 12 }),
    arrow({ element: floatingArrow, padding: 20 }),
    ...props.middleware,
  ]),
})

const side = computed(() => placement.value.split('-')[0] as 'top' | 'bottom' | 'left' | 'right')
const vertical = computed(() => side.value === 'top' || side.value === 'bottom')
const arrowStyles = computed(() => {
  const { x = 0, y = 0 } = middlewareData.value.arrow || {}
  switch (side.value) {
    case 'top':
      return { left: `${x}px`, bottom: '0px' }
    case 'bottom':
      return { left: `${x}px`, top: '0px' }
    case 'left':
      return { right: '0px', top: `${y}px` }
    default:
      return { left: '0px', top: `${y}px` }
  }
})
const tipStyle = computed(() => ({
  transform: `translate(-50%, -50%) rotate(${{ top: -90, bottom: 90, left: 180, right: 0 }[side.value]}deg)`,
}))

defineExpose({ update })
</script>

<template lang="pug">
.ui-tooltip-container.d-inline-flex.h-max-content(
  ref="reference" :aria-describedby="open ? tooltipId : undefined"
  @pointerenter="onPointerEnter" @pointerleave="onPointerLeave" @pointerdown="onPointerDown" @keydown="forgetTap" @touchstart="onTouch" @touchcancel="onTouchCancel" @click="onOpen"
)
  slot
  //- Keep a hover tooltip's link in tab order after its trigger.
  Teleport(to="body" :disabled="!teleport || (mode === 'hover' && !!$slots.link)")
    Transition(name="ui-tooltip")
      .ui-tooltip.w-max-content(
        v-if="open" :id="tooltipId" ref="floating"
        :style="{ ...floatingStyles, zIndex }" :class="[side, { mobile }]"
        :role="slots.link ? 'dialog' : 'tooltip'" :aria-label="slots.link && label ? $t(label) : undefined"
        @pointerenter="onFloatingPointerEnter" @pointerleave="onFloatingPointerLeave"
      )
        .ui-tooltip-content.flex.col-4.p-8-12.br-12.typ-12-18-500(:style="{ maxWidth }")
          .text
            slot(name="tooltip" :close="onClose")
              //- eslint-disable-next-line vue/no-v-html
              span(v-html="$t(label)")
          .ui-tooltip-link.typ-12-18-400(v-if="$slots.link")
            slot(name="link" :close="onClose")
        .ui-tooltip-arrow.position-absolute(ref="floatingArrow" :class="vertical ? 'w-8 h-5' : 'w-5 h-8'" :style="arrowStyles" aria-hidden="true")
          .ui-tooltip-tip.position-absolute.w-5.h-8(:style="tipStyle")
</template>

<style scoped>
.ui-tooltip-container {
  -webkit-tap-highlight-color: transparent;
}

.ui-tooltip {
  --uno: d-none;
  max-width: calc(100vw - 24px);

  &.mobile {
    --uno: d-block;
  }

  @screen md {
    --uno: d-block;
  }

  &.top {
    --uno: p-b-5;
  }

  &.bottom {
    --uno: p-t-5;
  }

  &.left {
    --uno: p-r-5;
  }

  &.right {
    --uno: p-l-5;
  }
}

.ui-tooltip-content {
  background: var(--cue-bg-surface-highest-inverse);
  color: var(--cue-content-neutral-on-inverse);
  overflow-wrap: anywhere;
}

.ui-tooltip-link {
  color: var(--cue-content-primary-on-inverse);

  :slotted(a),
  :slotted(button) {
    --uno: p-0 cursor-pointer text-left;
    border: 0;
    background: transparent;
    font: inherit;
    color: inherit;
    text-decoration: underline;
    text-underline-position: from-font;
  }
}

.ui-tooltip-tip {
  --uno: t-50p l-50p;
  background: var(--cue-bg-surface-highest-inverse);
  mask: url('../../../assets/svg/tooltip/tip.svg') center / contain no-repeat;
}

.ui-tooltip-enter-active,
.ui-tooltip-leave-active {
  transition: opacity 0.15s ease;
}

.ui-tooltip-enter-from,
.ui-tooltip-leave-to {
  --uno: opacity-0;
}

@media (prefers-reduced-motion: reduce) {
  .ui-tooltip-enter-active,
  .ui-tooltip-leave-active {
    transition: none;
  }
}
</style>
